<template>
  <div class="bg-light rounded mt-10 py-5 px-5 lg:px-10" data-testid="booking-payment-stripe-legacy">
    <div class="mb-4">
      <div class="flex flex-col gap-4 md:flex-row md:justify-between items-start md:items-center">
        <h3 class="flex text-xlx justify-start items-center gap-2">
          <span
              class="h-12 w-12 bg-dark text-light rounded-full inline-flex justify-center items-center leading-none">
            <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                  d="M5.875 5.625V7.5H12.125V5.625C12.125 3.90625 10.7188 2.5 9 2.5C7.24219 2.5 5.875 3.90625 5.875 5.625ZM3.375 7.5V5.625C3.375 2.53906 5.875 0 9 0C12.0859 0 14.625 2.53906 14.625 5.625V7.5H15.25C16.6172 7.5 17.75 8.63281 17.75 10V17.5C17.75 18.9062 16.6172 20 15.25 20H2.75C1.34375 20 0.25 18.9062 0.25 17.5V10C0.25 8.63281 1.34375 7.5 2.75 7.5H3.375Z"
                  fill="currentColor"/>
            </svg>
          </span>
          <span>Secure Payment</span>
        </h3>
        <img :src="`${$settings.theme_url}/assets/images/cards.png`" class="h-4 w-auto inline-block"
             alt="card logos, visa, mastercard, amex">
      </div>
    </div>
    <div class="flex flex-col gap-4">
      <VueStripeProvider :publishable-key="stripeKey" @load="onStripeLoad">
        <VueStripeElements
            v-if="stripeLoaded"
            v-slot="{ elements }"
            :options="elementsOptions"
            @ready="onElementsReady"
            ref="elms"
            class="py-3"
        >

        <div class="flex flex-col gap-4">
          <Field
              v-if="true"
              v-slot="{ field, errors }"
              v-model="creditCard.cardName"
              label="Name on card"
              rules="required|min:2"
              name="card_name"
          >
            <div>
              <FieldLabel for="card_name" :required="true">Name on card</FieldLabel>
              <input
                  id="card_name"
                  v-bind="field"
                  type="text"
                  autocomplete="card_name"
                  class="w-full leading-tight p-4 rounded-sm border bg-white shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                  :class="{ 'border-[#F54E4E]': errors.length }"
              />
              <FieldError :errors="errors" help="As appears on the your card"/>
            </div>
          </Field>
          <Field
              v-slot="{ field, errors, handleChange }"
              label="Card number"
              v-model="localCard.cardNumber"
              name="credit_card"
              rules="required|complete"
          >
            <div>
              <FieldLabel for="credit_card" :required="true">Card Number</FieldLabel>
                <VueStripeCardNumberElement
                    ref="card"
                    v-bind="field"
                    id="credit_card"
                    :elements="elements"
                    @change="handleChange"
                    class="w-full leading-tight p-4 rounded-sm border bg-white shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                    :class="{ 'border-[#F54E4E]': errors.length }"/>
              <FieldError :errors="errors" help="&nbsp;"/>
            </div>
          </Field>
          <div class="flex flex-col lg:flex-row gap-6">
            <div class="w-full lg:w-1/2">
              <FieldLabel for="expiry" :required="true">Expiry Date</FieldLabel>
              <Field
                  v-slot="{ field, errors, handleChange }"
                  v-model="localCard.cardExpiry"
                  label="Expiry Date"
                  name="expiry"
                  rules="required|complete"
                  as="div"
              >
                <VueStripeCardExpiryElement
                    id="expiry"
                    :elements="elements"
                    v-bind="field"
                    @change="handleChange"
                    class="w-full leading-tight p-4 rounded-sm border bg-white shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                    :class="{ 'border-[#F54E4E]': errors.length }" />
                <FieldError :errors="errors" />
              </Field>
            </div>
            <Field
                v-slot="{ field, errors, handleChange }"
                label="CVC"
                name="cvc"
                rules="required|complete"
                v-model="localCard.cardCvc"
            >
              <div class="customer-field mb-2">
                <FieldLabel for="cvv" :required="true">CVV Number</FieldLabel>
                <div class="flex-grow">
                  <VueStripeCardCvcElement
                      :elements="elements"
                      v-bind="field"
                      @change="handleChange"
                      class="w-full leading-tight p-4 rounded-sm border bg-white shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                      :class="{ 'border-[#F54E4E]': errors.length }"
                  />
                  <FieldError :errors="errors"/>
                </div>
              </div>
            </Field>
          </div>
        </div>

        <FieldError v-if="error !== ''" :errors="[error]"/>

        </VueStripeElements>
      </VueStripeProvider>
    </div>
  </div>
</template>
<script>
import { ref } from "vue"
import {
  VueStripeProvider,
  VueStripeElements,
  VueStripeCardNumberElement,
  VueStripeCardExpiryElement,
  VueStripeCardCvcElement
} from '@vue-stripe/vue-stripe'
import FieldLabel from "./FieldLabel.vue";
import {Field} from "vee-validate";
import FieldError from "./FieldError.vue";
import {mapWritableState} from "pinia";
import useStore from "../store";

export default {

  components: {
    FieldError, Field, FieldLabel,
    VueStripeProvider,
    VueStripeElements,
    VueStripeCardNumberElement,
    VueStripeCardExpiryElement,
    VueStripeCardCvcElement,
  },

  props: {
    config: {
      type: Object,
      default: () => ({})
    }
  },

  setup(props) {
    const stripeKey = props.config.token;

    const elementsOptions = ref({
      // https://stripe.com/docs/js/elements_object/create#stripe_elements-options
    })

    const stripeLoaded = ref(false)
    const stripeInstance = ref(null)
    const elementsInstance = ref(null)
    const card = ref()
    const elms = ref()

    const onStripeLoad = (stripe) => {
      stripeInstance.value = stripe
      stripeLoaded.value = true
    }

    const onElementsReady = (elements) => {
      elementsInstance.value = elements
    }

    return {
      stripeKey,
      stripeLoaded,
      elementsOptions,
      stripeInstance,
      elementsInstance,
      card,
      elms,
      onStripeLoad,
      onElementsReady,
    }
  },
  data(){
    return {
      localCard: {
        cardNumber: null,
        cardExpiry: null,
        cardCvc: null,
      },
      error: '',
      token: '',
    }
  },
  computed: {
    ...mapWritableState(useStore, [
      "creditCard",
    ]),
  },
  methods: {
    async generate() {

      const cardElement = this.card.$el
      const { token, error } = await this.stripeInstance.createToken(cardElement);

      if (error) {
        this.error = error.message;
        this.$emit('errors', [error.message])
        return false;
      }

      console.log('token', token)
      this.creditCard.cardToken = token.id;
      return true;
    },
  },
}
</script>
